import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { SocialLinksDark } from '../components/SocialLinks';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Check, Calendar, Users, Sparkles, Zap, Clock, DollarSign, ArrowRight, Package, Activity, Shield, FileText, Code, Key, Fingerprint, Lock, TrendingUp, Star, Link2, QrCode, Camera, Bell, Download, Mail, Menu, X } from 'lucide-react';
import { QRCodeSVG as QRCodeComponent } from 'qrcode.react';
import { getAllPlans, formatPrice } from '../config/pricing';

const LandingPage = () => {
  const navigate = useNavigate();
  const plans = getAllPlans(false); // Use demo pricing
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Add scroll animation observer
  useEffect(() => {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -100px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-fade-in-up');
        }
      });
    }, observerOptions);

    // Observe all sections with the class 'animate-on-scroll'
    document.querySelectorAll('.animate-on-scroll').forEach((el) => {
      observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <style>{`
        /* Smooth scroll animations */
        .animate-on-scroll {
          opacity: 0;
          transform: translateY(30px);
          transition: all 0.8s cubic-bezier(0.4, 0, 0.2, 1);
        }
        
        .animate-fade-in-up {
          opacity: 1;
          transform: translateY(0);
        }

        /* Smooth scroll behavior */
        html {
          scroll-behavior: smooth;
        }
      `}</style>
      
      {/* Header/Nav */}
      <header className="border-b border-gray-100 sticky top-0 z-50 bg-white/95 backdrop-blur-sm pt-[env(safe-area-inset-top)]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate('/')}>
              <img 
                src="/logo-icon.png" 
                alt="BookingFlows" 
                className="w-10 h-10 rounded-xl object-cover"
              />
              <h1 className="text-2xl font-bold text-gray-900 font-brand">
                Booking<span className="text-blue-600">Flows</span>
              </h1>
            </div>
            
            {/* Desktop Buttons - Hidden on mobile */}
            <div className="hidden md:flex items-center gap-3">
              <Button 
                variant="ghost" 
                onClick={() => navigate('/login')}
                className="btn-landing-ghost"
              >
                Log In
              </Button>
              <Button 
                onClick={() => navigate('/signup')}
                className="bg-blue-600 hover:bg-blue-700 text-white font-medium px-5 py-2.5 rounded-lg shadow-sm hover:shadow-md transition-all duration-200"
              >
                Request Demo
              </Button>
            </div>
            
            {/* Mobile Menu Button - Visible on mobile only */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors"
            >
              {mobileMenuOpen ? (
                <X className="w-6 h-6 text-gray-700" />
              ) : (
                <Menu className="w-6 h-6 text-gray-700" />
              )}
            </button>
          </div>
          
          {/* Mobile Dropdown Menu */}
          {mobileMenuOpen && (
            <div className="md:hidden pb-4 space-y-2 border-t border-gray-100 pt-4">
              <Button 
                variant="ghost" 
                onClick={() => {
                  navigate('/login');
                  setMobileMenuOpen(false);
                }}
                className="w-full btn-landing-ghost justify-start"
              >
                Log In
              </Button>
              <Button 
                onClick={() => {
                  navigate('/signup');
                  setMobileMenuOpen(false);
                }}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-2.5 rounded-lg"
              >
                Request Demo
              </Button>
            </div>
          )}
        </div>
      </header>

      {/* Hero Section */}
      <section className="max-w-7xl mx-auto container-px section-py">
        <div className="text-center max-w-4xl mx-auto animate-on-scroll">
          <h2 className="text-hero font-brand mb-8">
            Book More. Schedule Less.{' '}
            <span className="text-blue-600">Grow Faster.</span>
          </h2>
          <p className="text-hero-sub font-brand mb-12">
            Take bookings online. Get paid automatically. Manage your team—all in one place. Built for tour operators, rental companies, museums, music venues, and any business that takes reservations.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              onClick={() => navigate('/signup')}
              size={null}
              className="bg-blue-600 hover:bg-blue-700 text-white font-medium text-lg px-8 py-4 rounded-lg shadow-md hover:shadow-lg transition-all duration-200"
            >
              Request Demo <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
            <Button 
              variant="outline"
              size={null}
              onClick={() => navigate('/login')}
              className="btn-landing-secondary text-lg px-8 py-4"
            >
              Log In
            </Button>
          </div>
          <p className="text-caption font-brand mt-6">Free setup • No commitment required</p>
        </div>
      </section>


      {/* Combined Booking Software Section - Replacing both previous sections */}
      <section className="py-24 bg-white animate-on-scroll">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-block mb-4">
              <span className="badge-landing bg-purple-100 text-purple-700">
                Tours • Rentals • Venues • Museums • Experiences & More
              </span>
            </div>
            <h3 className="landing-section-title">
              Simple Booking Software That Does More
            </h3>
            <p className="landing-section-subtitle max-w-3xl mx-auto">
              Handle online bookings, payments, and operations. We built it for booking—then added the tools you actually need.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-12">
            {/* Card 1: Online Bookings & Payments */}
            <div className="landing-card hover:border-purple-200">
              <div className="icon-container-lg bg-gradient-to-br from-purple-500 to-pink-600">
                <Sparkles className="w-8 h-8 text-white" />
              </div>
              <h4 className="landing-card-title">Online Bookings & Payments</h4>
              <p className="landing-card-desc">
                Customers book 24/7. Payments go straight to your account. They get a shareable ticket with all their booking details.
              </p>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">Online Booking Tools</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">Secure payments</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">Shareable customer tickets</span>
                </li>
              </ul>
            </div>

            {/* Card 2: Operations & Team Tools */}
            <div className="landing-card hover:border-blue-200">
              <div className="icon-container-lg bg-gradient-to-br from-blue-500 to-cyan-600">
                <Users className="w-8 h-8 text-white" />
              </div>
              <h4 className="landing-card-title">Operations & Team Tools</h4>
              <p className="landing-card-desc">
                Staff scheduling, digital waivers, role permissions, and team coordination. The operational stuff that keeps things running smooth.
              </p>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">AI-assisted staff scheduling</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">Digital waivers with verification</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">Configure roles & permissions</span>
                </li>
              </ul>
            </div>

            {/* Card 3: Secure & Reliable */}
            <div className="landing-card hover:border-green-200">
              <div className="icon-container-lg bg-gradient-to-br from-green-500 to-emerald-600">
                <Shield className="w-8 h-8 text-white" />
              </div>
              <h4 className="landing-card-title">Secure & Reliable</h4>
              <p className="landing-card-desc">
                99.9% uptime so you're always open. Your data stays in your own database. Plus a simple CRM to help you grow.
              </p>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">99.9% uptime guarantee</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">Private database (your data, your control)</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">Built-in customer management</span>
                </li>
              </ul>
            </div>
          </div>

          {/* Cheeky footer text */}
          <div className="text-center mt-12">
            <p className="text-gray-600 text-lg">
              ...we even send thank you emails on your behalf.
            </p>
          </div>
        </div>
      </section>

      {/* Waiver Management Section - Move this EARLIER, remove old gradient callout */}
      <section className="py-24 bg-gradient-to-br from-gray-900 to-gray-800 text-white animate-on-scroll">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h3 className="landing-section-title text-white">
              Digital Waivers That Actually Work
            </h3>
            <p className="landing-section-subtitle text-gray-300 max-w-3xl mx-auto">
              Protect your business with verified digital waivers. We collect device fingerprints and IP addresses—so you have proof if you ever need it.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <div className="flex items-start gap-4">
                <div className="bg-blue-500 p-3 rounded-lg flex-shrink-0">
                  <Fingerprint className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h4 className="text-xl font-bold text-white mb-2">Device Verification</h4>
                  <p className="text-gray-300">
                    We automatically capture device fingerprints, IP addresses, and browser info when customers sign. Full audit trail for legal protection.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="bg-green-500 p-3 rounded-lg flex-shrink-0">
                  <Lock className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h4 className="text-xl font-bold text-white mb-2">PIN-Based Tickets</h4>
                  <p className="text-gray-300">
                    Your guides get a unique PIN for each trip. Customers enter the PIN to access their ticket—guides instantly know who signed the waiver. Simple, secure.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="bg-purple-500 p-3 rounded-lg flex-shrink-0">
                  <FileText className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h4 className="text-xl font-bold text-white mb-2">Custom Waiver Text</h4>
                  <p className="text-gray-300">
                    Write your own waiver text. Multi-signature support for groups. Customers sign on their phone before the trip.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-gray-800 rounded-2xl p-8 border border-gray-700">
              <div className="bg-white rounded-xl p-6 mb-4">
                <div className="flex items-center justify-between mb-4">
                  <h5 className="text-gray-900 font-bold">Booking Waiver</h5>
                  <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">Verified</span>
                </div>
                <div className="space-y-3 text-sm text-gray-600">
                  <div className="flex justify-between">
                    <span>Device:</span>
                    <span className="font-mono">iPhone 15 Pro</span>
                  </div>
                  <div className="flex justify-between">
                    <span>IP Address:</span>
                    <span className="font-mono">203.0.113.45</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Location:</span>
                    <span className="font-mono">Austin, TX</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Timestamp:</span>
                    <span className="font-mono">11/11/25 4:32 PM</span>
                  </div>
                </div>
              </div>
              <p className="text-sm text-gray-400 text-center">
                ✓ All verification data stored securely for legal compliance
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Customer Ticket Feature */}
      <section className="py-24 bg-gradient-to-br from-gray-50 to-white animate-on-scroll">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h3 className="landing-section-title mb-6">
              Shareable Customer Tickets
            </h3>
            <p className="landing-section-subtitle max-w-3xl mx-auto">
              Give your customers everything they need in one simple link. No app downloads, no confusion.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-12 items-center">
            {/* Features List */}
            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="bg-blue-500 p-3 rounded-lg flex-shrink-0">
                  <Check className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h4 className="landing-step-title">All Booking Details in One Place</h4>
                  <p className="text-gray-600">
                    Meeting location, time, asset name, team_member info, and emergency contacts—everything your customer needs to show up prepared.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="bg-green-500 p-3 rounded-lg flex-shrink-0">
                  <Lock className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h4 className="landing-step-title">PIN-Protected Waiver Access</h4>
                  <p className="text-gray-600">
                    Customers sign waivers digitally using a secure PIN. TeamMembers instantly know who signed—no paperwork, no hassle.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="bg-purple-500 p-3 rounded-lg flex-shrink-0">
                  <ArrowRight className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h4 className="landing-step-title">Instantly Shareable</h4>
                  <p className="text-gray-600">
                    Send via text, email, or any messaging app. Works on any device—no app required. Customers get the link, they're ready to go.
                  </p>
                </div>
              </div>
            </div>

            {/* Ticket Mockup */}
            <div className="relative">
              <div className="bg-white rounded-2xl shadow-2xl border-l-4 border-blue-600 p-8 max-w-md mx-auto">
                {/* Header */}
                <div className="flex items-center justify-between mb-6 pb-4 border-b">
                  <div className="flex items-center gap-2">
                    <div className="bg-blue-600 p-2 rounded-lg">
                      <Calendar className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 uppercase tracking-wide">Booking ID</p>
                      <p className="font-mono font-bold text-blue-600">BF-1CWQ4T</p>
                    </div>
                  </div>
                  <span className="text-xs bg-green-100 text-green-800 px-3 py-1 rounded-full font-semibold">Confirmed</span>
                </div>

                {/* Company Name */}
                <h3 className="font-bold text-2xl text-gray-900 mb-6">Austin ATV Rentals</h3>

                {/* Booking Details */}
                <div className="space-y-4 mb-6">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Asset</p>
                      <p className="font-semibold text-gray-900">Sunset Cruiser</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">TeamMember</p>
                      <p className="font-semibold text-gray-900">John S.</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Date</p>
                      <p className="font-semibold text-gray-900">Sat, Nov 15</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Time</p>
                      <p className="font-semibold text-gray-900">2:00 PM</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Duration</p>
                      <p className="font-semibold text-gray-900">3 hours</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Guests</p>
                      <p className="font-semibold text-gray-900">8 people</p>
                    </div>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Meeting Location</p>
                    <p className="font-semibold text-gray-900">Trailhead Parking</p>
                  </div>
                </div>

                {/* Emergency */}
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-4">
                  <p className="text-xs font-semibold text-orange-900 mb-1">Emergency Contact</p>
                  <p className="text-sm font-mono text-orange-700">+1 (512) 555-ASSET</p>
                </div>

                {/* Who's Signed */}
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                  <p className="text-xs font-semibold text-green-900 mb-2 uppercase tracking-wide">Who's Signed the Waiver</p>
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <Check className="w-4 h-4 text-green-600" />
                      <span className="text-sm text-green-800">John Smith</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Check className="w-4 h-4 text-green-600" />
                      <span className="text-sm text-green-800">Sarah Johnson</span>
                    </div>
                    <div className="flex items-center gap-2 opacity-50 mt-2 pt-2 border-t border-green-200">
                      <div className="w-4 h-4 border-2 border-gray-400 rounded"></div>
                      <span className="text-sm text-gray-600">6 more guests need to sign</span>
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div className="space-y-2">
                  <button className="btn-card-primary">
                    <Lock className="w-4 h-4" />
                    <span>Sign Waiver</span>
                    <span className="absolute right-4 text-xs opacity-75">2/8</span>
                  </button>
                  <button className="btn-card-secondary">
                    <Link2 className="w-4 h-4" />
                    Copy Ticket Link
                  </button>
                </div>
                
                {/* Footer */}
                <div className="text-center mt-6 pt-4 border-t">
                  <p className="text-xs text-gray-500">
                    Powered by <span className="font-semibold">BookingFlows</span>
                  </p>
                </div>
              </div>

              <div className="absolute -bottom-4 -right-4 bg-green-500 text-white px-4 py-2 rounded-full text-sm font-bold shadow-lg">
                Works on any device
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Review Collection Feature */}
      <section className="py-24 bg-gradient-to-b from-white to-gray-50 animate-on-scroll">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-block mb-4">
              <span className="bg-yellow-100 text-yellow-700 px-3 py-1.5 sm:px-4 sm:py-2 rounded-full text-xs sm:text-sm font-semibold">
                ⭐ Build Social Proof
              </span>
            </div>
            <h3 className="landing-section-title mb-4">
              Make 1-Click Reviews!
            </h3>
            <p className="landing-section-subtitle max-w-3xl mx-auto mb-6">
              Add your Google Reviews, TripAdvisor, or Yelp link. Toggle it on in settings. Customers see a review button on their ticket—easy 1-click reviews.
            </p>
            <div className="bg-blue-50 border-2 border-blue-200 rounded-2xl p-6 max-w-3xl mx-auto">
              <p className="text-2xl text-gray-900 font-semibold mb-2">
                ✨ Tickets Are Fully Customizable
              </p>
              <p className="text-gray-700">
                Enable review buttons, QR code check-ins, or both! Don't need waivers? Remove them. Every feature is optional—build the perfect ticket for your business.
              </p>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-12 items-center">
            {/* Left: Real Ticket with Review Button */}
            <div>
              <h4 className="landing-card-title text-center">Customer Ticket</h4>
              
              {/* Copy EXACT design from shareable tickets section */}
              <div className="bg-white rounded-2xl shadow-2xl border-l-4 border-blue-600 p-8 max-w-md mx-auto">
                {/* Header */}
                <div className="flex items-center justify-between mb-6 pb-4 border-b">
                  <div className="flex items-center gap-2">
                    <div className="bg-blue-600 p-2 rounded-lg">
                      <Calendar className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 uppercase tracking-wide">Booking ID</p>
                      <p className="font-mono font-bold text-blue-600">BF-ABC123</p>
                    </div>
                  </div>
                  <span className="text-xs bg-green-100 text-green-800 px-3 py-1 rounded-full font-semibold">Confirmed</span>
                </div>

                {/* Company Name */}
                <h3 className="font-bold text-2xl text-gray-900 mb-6">BOATX</h3>

                {/* Booking Details */}
                <div className="space-y-4 mb-6">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Date</p>
                      <p className="font-semibold text-gray-900">Sat, Nov 29</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Time</p>
                      <p className="font-semibold text-gray-900">2:00 PM CST</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Duration</p>
                      <p className="font-semibold text-gray-900">3 hours</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Guests</p>
                      <p className="font-semibold text-gray-900">4 people</p>
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div className="space-y-2">
                  <button className="btn-card-primary">
                    <Lock className="w-4 h-4" />
                    <span>Sign Waiver</span>
                  </button>
                  <button className="w-full bg-gradient-to-r from-yellow-400 to-orange-500 text-white py-3 rounded-lg font-semibold hover:opacity-90 transition-all flex items-center justify-center gap-2">
                    <Star className="w-4 h-4 fill-white" />
                    Leave a Review
                  </button>
                  <button className="btn-card-secondary">
                    <Link2 className="w-4 h-4" />
                    Copy Ticket Link
                  </button>
                </div>
              </div>
            </div>
            <div>
              <h4 className="landing-card-title">Configure in 30 Seconds:</h4>
              <div className="space-y-4">
                <div className="flex items-start gap-4">
                  <div className="bg-blue-600 w-8 h-8 rounded-full flex items-center justify-center text-white font-bold flex-shrink-0">
                    1
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">Add Your Review Link</p>
                    <p className="text-sm text-gray-600">Paste your Google Reviews, TripAdvisor, or Yelp URL</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="bg-blue-600 w-8 h-8 rounded-full flex items-center justify-center text-white font-bold flex-shrink-0">
                    2
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">Toggle It On</p>
                    <p className="text-sm text-gray-600">Enable review collection from your dashboard</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="bg-blue-600 w-8 h-8 rounded-full flex items-center justify-center text-white font-bold flex-shrink-0">
                    3
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">Done!</p>
                    <p className="text-sm text-gray-600">Button appears on every customer ticket automatically</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* QR Code Check-In Section - Clean & Professional */}
      <section className="py-24 bg-white animate-on-scroll">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-block mb-4">
              <span className="bg-purple-100 text-purple-700 px-3 py-1.5 sm:px-4 sm:py-2 rounded-full text-xs sm:text-sm font-semibold">
                QR Check-In System
              </span>
            </div>
            <h3 className="landing-section-title mb-6">
              Professional Customer Check-In
            </h3>
            <p className="landing-section-subtitle max-w-3xl mx-auto">
              Perfect for tours, venues, events, and group activities. Staff scan QR codes to check in guests instantly.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-16 items-center">
            {/* Left: How It Works */}
            <div className="space-y-8">
              <h4 className="landing-card-title">How It Works</h4>
              
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="bg-purple-600 w-10 h-10 rounded-lg flex items-center justify-center text-white font-bold flex-shrink-0">
                    1
                  </div>
                  <div>
                    <h5 className="landing-step-title">Customer Shows Ticket</h5>
                    <p className="text-gray-600">
                      Customer displays their QR code
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="bg-blue-600 w-10 h-10 rounded-lg flex items-center justify-center text-white font-bold flex-shrink-0">
                    2
                  </div>
                  <div>
                    <h5 className="landing-step-title">Staff Scans with Phone</h5>
                    <p className="text-gray-600">
                      Quick scan, no special equipment needed
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="bg-green-600 w-10 h-10 rounded-lg flex items-center justify-center text-white font-bold flex-shrink-0">
                    3
                  </div>
                  <div>
                    <h5 className="landing-step-title">Enter PIN</h5>
                    <p className="text-gray-600">
                      Secure verification in seconds
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="bg-purple-600 w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Check className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h5 className="landing-step-title">Instant Verification</h5>
                    <p className="text-gray-600">
                      Check-in complete, everyone's ready to go
                    </p>
                  </div>
                </div>
              </div>

              {/* Perfect For */}
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
                <h5 className="font-bold text-blue-900 mb-3">Perfect For:</h5>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-blue-600" />
                    <span className="text-gray-700">Music Venues</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-blue-600" />
                    <span className="text-gray-700">Tour Operators</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-blue-600" />
                    <span className="text-gray-700">Museums & Galleries</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-blue-600" />
                    <span className="text-gray-700">Rental Companies</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Right: Real Customer Ticket */}
            <div className="order-1 md:order-2">
              <h4 className="landing-card-title text-center">Customer Ticket</h4>
              
              {/* Copy EXACT design from shareable tickets section */}
              <div className="bg-white rounded-2xl shadow-2xl border-l-4 border-blue-600 p-8 max-w-md mx-auto">
                {/* Header */}
                <div className="flex items-center justify-between mb-6 pb-4 border-b">
                  <div className="flex items-center gap-2">
                    <div className="bg-blue-600 p-2 rounded-lg">
                      <Calendar className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 uppercase tracking-wide">Booking ID</p>
                      <p className="font-mono font-bold text-blue-600">BF-ABC123</p>
                    </div>
                  </div>
                  <span className="text-xs bg-green-100 text-green-800 px-3 py-1 rounded-full font-semibold">Confirmed</span>
                </div>

                {/* Company Name */}
                <h3 className="font-bold text-2xl text-gray-900 mb-6">MUSIC FESTIVAL VIP</h3>

                {/* Booking Details */}
                <div className="space-y-4 mb-6">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Date</p>
                      <p className="font-semibold text-gray-900">Fri, Nov 29</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Time</p>
                      <p className="font-semibold text-gray-900">7:00 PM CST</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Duration</p>
                      <p className="font-semibold text-gray-900">8 hours</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Guests</p>
                      <p className="font-semibold text-gray-900">2 people</p>
                    </div>
                  </div>
                </div>

                {/* Actions with QR Code */}
                <div className="space-y-2">
                  <button className="btn-card-primary">
                    <Lock className="w-4 h-4" />
                    <span>Sign Waiver</span>
                  </button>
                  <button className="w-full bg-purple-600 text-white py-3 rounded-lg font-semibold hover:bg-purple-700 transition-colors flex items-center justify-center gap-2">
                    <QrCode className="w-4 h-4" />
                    <span>Show QR Code</span>
                  </button>
                  <button className="btn-card-secondary">
                    <Link2 className="w-4 h-4" />
                    Copy Ticket Link
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>


      {/* Objection Handling - Why BookingFlows? */}
      <section className="py-20 bg-gray-50 animate-on-scroll">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h3 className="landing-section-title mb-4">
              Why BookingFlows?
            </h3>
            <p className="landing-section-subtitle">
              The answers you're looking for
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {/* Objection 1: AI Control */}
            <div className="bg-gradient-to-br from-blue-50 to-white p-8 rounded-2xl border border-blue-100">
              <div className="flex items-start gap-4">
                <div className="bg-blue-600 p-3 rounded-lg flex-shrink-0">
                  <Sparkles className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h4 className="landing-step-title">
                    "What if the AI messes up?"
                  </h4>
                  <p className="text-gray-700 font-semibold mb-2">
                    You approve everything. AI just saves you time.
                  </p>
                  <p className="text-gray-600 text-sm">
                    AI suggests optimal schedules in seconds—you review and approve with one click. Override any suggestion manually anytime. Think of it as a smart assistant, not a replacement.
                  </p>
                </div>
              </div>
            </div>

            {/* Objection 2: Cost */}
            <div className="bg-gradient-to-br from-green-50 to-white p-8 rounded-2xl border border-green-100">
              <div className="flex items-start gap-4">
                <div className="bg-green-600 p-3 rounded-lg flex-shrink-0">
                  <DollarSign className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h4 className="landing-step-title">
                    "Isn't this expensive?"
                  </h4>
                  <p className="text-gray-700 font-semibold mb-2">
                    Your customers pay the platform fee. We charge 3%.
                  </p>
                  <p className="text-gray-600 text-sm">
                    Far less than competitors (8%+), leaving more money for you. No upfront costs, no monthly minimums—you only make money when you make money.
                  </p>
                </div>
              </div>
            </div>

            {/* Objection 3: Integration */}
            <div className="bg-gradient-to-br from-cyan-50 to-white p-8 rounded-2xl border border-cyan-100">
              <div className="flex items-start gap-4">
                <div className="bg-cyan-600 p-3 rounded-lg flex-shrink-0">
                  <Code className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h4 className="landing-step-title">
                    "Will this work with my existing systems?"
                  </h4>
                  <p className="text-gray-700 font-semibold mb-2">
                    Integrates with everything.
                  </p>
                  <p className="text-gray-600 text-sm">
                    Full REST API and embeddable widgets. Works with your website, QuickBooks, POS systems, or custom software. No tech team required—just copy/paste.
                  </p>
                </div>
              </div>
            </div>

            {/* Objection 4: Competitors */}
            <div className="bg-gradient-to-br from-purple-50 to-white p-8 rounded-2xl border border-purple-100">
              <div className="flex items-start gap-4">
                <div className="bg-purple-600 p-3 rounded-lg flex-shrink-0">
                  <Sparkles className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h4 className="landing-step-title">
                    "Why not use FareHarbor or competitors?"
                  </h4>
                  <p className="text-gray-700 font-semibold mb-2">
                    Go ahead. Use them.
                  </p>
                  <p className="text-gray-600 text-sm">
                    Just don't be surprised when businesses using us surpass you with AI scheduling, enforceable waivers, lower fees, a simpler interface, and an owner who used to work at a lab. We're built different.
                  </p>
                </div>
              </div>
            </div>

            {/* Objection 5: Setup Time */}
            <div className="bg-gradient-to-br from-orange-50 to-white p-8 rounded-2xl border border-orange-100">
              <div className="flex items-start gap-4">
                <div className="bg-orange-600 p-3 rounded-lg flex-shrink-0">
                  <Clock className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h4 className="landing-step-title">
                    "Is setup complicated?"
                  </h4>
                  <p className="text-gray-700 font-semibold mb-2">
                    5 minutes to go live.
                  </p>
                  <p className="text-gray-600 text-sm">
                    Add your services, paste our widget code on your website, done. No coding required. Start taking bookings in minutes, not weeks.
                  </p>
                </div>
              </div>
            </div>

            {/* Objection 6: Free Trial */}
            <div className="bg-gradient-to-br from-pink-50 to-white p-8 rounded-2xl border border-pink-100">
              <div className="flex items-start gap-4">
                <div className="bg-pink-600 p-3 rounded-lg flex-shrink-0">
                  <Check className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h4 className="landing-step-title">
                    "Can I try it first?"
                  </h4>
                  <p className="text-gray-700 font-semibold mb-2">
                    Absolutely. It's always free to use.
                  </p>
                  <p className="text-gray-600 text-sm">
                    No monthly fees. No setup costs. Just a small 3% fee when customers book. Try all features risk-free—cancel anytime.
                  </p>
                </div>
              </div>
            </div>

            {/* Objection 7: Data Security */}
            <div className="bg-gradient-to-br from-indigo-50 to-white p-8 rounded-2xl border border-indigo-100">
              <div className="flex items-start gap-4">
                <div className="bg-indigo-600 p-3 rounded-lg flex-shrink-0">
                  <Shield className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h4 className="landing-step-title">
                    "Is my data safe?"
                  </h4>
                  <p className="text-gray-700 font-semibold mb-2">
                    Encrypted and secure.
                  </p>
                  <p className="text-gray-600 text-sm">
                    Data encrypted at rest. Your customer data is yours—we never sell it. Industry-standard security practices.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Big CTA after objections */}
          <div className="text-center mt-16 px-4">
            <Button 
              size="lg"
              onClick={() => navigate('/signup')}
              className="bg-blue-600 hover:bg-blue-700 text-white font-medium text-lg sm:text-xl px-8 sm:px-12 py-4 sm:py-5 rounded-lg shadow-lg shadow-blue-200 hover:shadow-xl transition-all duration-200 w-full sm:w-auto"
            >
              <span className="hidden sm:inline">Request Your Free Demo</span>
              <span className="sm:hidden">Request Demo</span>
              <ArrowRight className="ml-2 w-4 h-4 sm:w-5 sm:h-5" />
            </Button>
            <p className="text-sm text-gray-500 mt-4">
              Join businesses saving 10+ hours per week with BookingFlows
            </p>
          </div>
        </div>
      </section>

      {/* Marketplace Integrations Section */}
      <section className="py-20 bg-gray-50 animate-on-scroll">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h3 className="landing-section-title mb-4">
              Expand Your Reach
            </h3>
            <p className="landing-section-subtitle max-w-2xl mx-auto">
              Connect to leading travel marketplaces and get bookings from millions of travelers worldwide.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            {/* GetYourGuide */}
            <div className="bg-white rounded-xl p-6 border border-gray-200 hover:shadow-lg transition-shadow">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-lg overflow-hidden">
                  <img src="/images/integrations/gyg-icon.jpg" alt="GetYourGuide" className="w-full h-full object-cover" />
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900">GetYourGuide</h4>
                  <span className="text-xs text-green-600 font-medium">Connected</span>
                </div>
              </div>
              <p className="text-sm text-gray-600">
                List your tours on GetYourGuide and reach millions of travelers globally.
              </p>
            </div>

            {/* Viator */}
            <div className="bg-white rounded-xl p-6 border border-gray-200 hover:shadow-lg transition-shadow">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-lg overflow-hidden bg-white">
                  <img src="/images/integrations/viator-icon.png" alt="Viator" className="w-full h-full object-contain" />
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900">Viator</h4>
                  <span className="text-xs text-green-600 font-medium">Connected</span>
                </div>
              </div>
              <p className="text-sm text-gray-600">
                Tap into Viator's massive travel marketplace and grow your bookings.
              </p>
            </div>

            {/* LetsBatch */}
            <div className="bg-white rounded-xl p-6 border border-gray-200 hover:shadow-lg transition-shadow">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-lg overflow-hidden bg-black p-1">
                  <img src="/images/integrations/letsbatch-icon.png" alt="LetsBatch" className="w-full h-full object-contain" />
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900">LetsBatch</h4>
                  <span className="text-xs text-green-600 font-medium">Connected</span>
                </div>
              </div>
              <p className="text-sm text-gray-600">
                Direct traffic model for adventure seekers and group experiences.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* API & Integrations Section - Technical credibility */}
      <section className="py-24 pb-32 bg-white animate-on-scroll">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h3 className="landing-section-title mb-6">
              Developer-Friendly API
            </h3>
            <p className="landing-section-subtitle max-w-3xl mx-auto">
              Integrate BookingFlows with your existing software. Full REST API and comprehensive documentation.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto items-start justify-center px-2 sm:px-0">
            {/* Terminal Mock */}
            <div className="bg-gray-900 rounded-xl p-4 sm:p-6 shadow-2xl mx-auto w-full max-w-[320px] sm:max-w-lg md:max-w-none overflow-hidden">
              <div className="flex items-center gap-2 mb-4">
                <div className="flex gap-2">
                  <div className="w-3 h-3 rounded-full bg-red-500"></div>
                  <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                  <div className="w-3 h-3 rounded-full bg-green-500"></div>
                </div>
                <span className="text-xs text-gray-400 ml-2">api_request.sh</span>
              </div>
              <div className="font-mono text-xs sm:text-sm space-y-2 overflow-x-auto">
                <div className="text-gray-400 whitespace-nowrap">$ <span className="text-green-400">curl</span> <span className="text-blue-400 break-all sm:break-normal">https://api.bookingflows.com/bookings</span></div>
                <div className="text-gray-300 pl-2">{`{`}</div>
                <div className="text-gray-300 pl-4">"<span className="text-yellow-400">status</span>": "<span className="text-green-400">success</span>",</div>
                <div className="text-gray-300 pl-4">"<span className="text-yellow-400">bookings</span>": [</div>
                <div className="text-gray-300 pl-6">{`{`}</div>
                <div className="text-gray-300 pl-8">"<span className="text-yellow-400">id</span>": "<span className="text-purple-400">bk_abc123</span>",</div>
                <div className="text-gray-300 pl-8">"<span className="text-yellow-400">customer</span>": "<span className="text-blue-300">John Smith</span>",</div>
                <div className="text-gray-300 pl-8">"<span className="text-yellow-400">date</span>": "<span className="text-blue-300">2025-11-20</span>",</div>
                <div className="text-gray-300 pl-8">"<span className="text-yellow-400">status</span>": "<span className="text-green-400">confirmed</span>"</div>
                <div className="text-gray-300 pl-6">{`}`}</div>
                <div className="text-gray-300 pl-4">]</div>
                <div className="text-gray-300 pl-2">{`}`}</div>
                <div className="text-green-400 mt-4">✓ <span className="text-gray-400">200 OK</span></div>
              </div>
            </div>

            {/* API Features List */}
            <div className="space-y-6">
              <div>
                <h4 className="landing-card-title">Simple REST Endpoints</h4>
                <p className="text-gray-600 mb-6">
                  Access all features programmatically. Create bookings, manage schedules, sync data—clean JSON responses.
                </p>
                <div className="space-y-3">
                  <div className="flex items-center gap-3 bg-white p-4 rounded-lg border border-gray-200">
                    <code className="text-sm font-mono text-blue-600 font-semibold">GET</code>
                    <span className="text-gray-700">/api/bookings</span>
                  </div>
                  <div className="flex items-center gap-3 bg-white p-4 rounded-lg border border-gray-200">
                    <code className="text-sm font-mono text-green-600 font-semibold">POST</code>
                    <span className="text-gray-700">/api/bookings</span>
                  </div>
                  <div className="flex items-center gap-3 bg-white p-4 rounded-lg border border-gray-200">
                    <code className="text-sm font-mono text-orange-600 font-semibold">PUT</code>
                    <span className="text-gray-700">/api/team/{`{id}`}</span>
                  </div>
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-5">
                <h5 className="font-semibold text-gray-900 mb-2">Built for Developers</h5>
                <ul className="space-y-2 text-sm text-gray-700">
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-blue-600 flex-shrink-0" />
                    <span>1,000 requests/hour per API key</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-blue-600 flex-shrink-0" />
                    <span>Rate limiting & usage analytics</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-blue-600 flex-shrink-0" />
                    <span>No tech team needed for basic setup</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Security & Compliance Section */}
      <section className="py-24 bg-gradient-to-b from-gray-900 to-gray-800 text-white animate-on-scroll">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-block mb-4">
              <span className="bg-green-500/20 text-green-400 px-4 py-2 rounded-full text-sm font-semibold border border-green-500/30">
                🔒 Enterprise-Grade Security
              </span>
            </div>
            <h3 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Your Data, Protected
            </h3>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Built for businesses that take privacy seriously. We're working toward the highest compliance standards.
            </p>
          </div>

          {/* Security Features Grid */}
          <div className="grid md:grid-cols-3 gap-8 mb-16">
            {/* Encryption */}
            <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/10 hover:border-green-500/30 transition-all">
              <div className="w-14 h-14 bg-green-500/20 rounded-xl flex items-center justify-center mb-6">
                <Lock className="w-7 h-7 text-green-400" />
              </div>
              <h4 className="text-xl font-semibold text-white mb-3">End-to-End Encryption</h4>
              <p className="text-gray-400 leading-relaxed">
                <span className="text-green-400 font-medium">AES-256 encryption at rest</span> and <span className="text-green-400 font-medium">TLS 1.3 in transit</span>. Your customer data, payment info, and signed waivers are protected with military-grade encryption.
              </p>
            </div>

            {/* EU Hosting */}
            <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/10 hover:border-blue-500/30 transition-all">
              <div className="w-14 h-14 bg-blue-500/20 rounded-xl flex items-center justify-center mb-6">
                <Shield className="w-7 h-7 text-blue-400" />
              </div>
              <h4 className="text-xl font-semibold text-white mb-3">EU Data Residency</h4>
              <p className="text-gray-400 leading-relaxed">
                Database and APIs hosted in <span className="text-blue-400 font-medium">Frankfurt, Germany</span>—one of the world's strictest data privacy jurisdictions. Your data never leaves the EU.
              </p>
            </div>

            {/* Compliance */}
            <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/10 hover:border-purple-500/30 transition-all">
              <div className="w-14 h-14 bg-purple-500/20 rounded-xl flex items-center justify-center mb-6">
                <FileText className="w-7 h-7 text-purple-400" />
              </div>
              <h4 className="text-xl font-semibold text-white mb-3">Compliance-Ready</h4>
              <p className="text-gray-400 leading-relaxed">
                Building toward <span className="text-purple-400 font-medium">GDPR</span>, <span className="text-purple-400 font-medium">CCPA</span>, and <span className="text-purple-400 font-medium">SOC 2</span> compliance standards. We take privacy regulations seriously.
              </p>
            </div>
          </div>

          {/* Trust Badges */}
          <div className="flex flex-wrap justify-center items-center gap-6 md:gap-12 pt-8 border-t border-white/10">
            <div className="flex items-center gap-3 text-gray-400">
              <div className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center">
                <Lock className="w-5 h-5 text-green-400" />
              </div>
              <div className="text-left">
                <div className="text-sm font-medium text-white">256-bit SSL</div>
                <div className="text-xs text-gray-500">Encrypted Connection</div>
              </div>
            </div>
            
            <div className="flex items-center gap-3 text-gray-400">
              <div className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center">
                <Shield className="w-5 h-5 text-blue-400" />
              </div>
              <div className="text-left">
                <div className="text-sm font-medium text-white">GDPR Ready</div>
                <div className="text-xs text-gray-500">EU Compliant</div>
              </div>
            </div>
            
            <div className="flex items-center gap-3 text-gray-400">
              <div className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center">
                <Activity className="w-5 h-5 text-purple-400" />
              </div>
              <div className="text-left">
                <div className="text-sm font-medium text-white">99.9% Uptime</div>
                <div className="text-xs text-gray-500">Enterprise SLA</div>
              </div>
            </div>
            
            <div className="flex items-center gap-3 text-gray-400">
              <div className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center">
                <Fingerprint className="w-5 h-5 text-orange-400" />
              </div>
              <div className="text-left">
                <div className="text-sm font-medium text-white">SOC 2 Roadmap</div>
                <div className="text-xs text-gray-500">In Progress</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-24 bg-gray-50 animate-on-scroll">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <div className="inline-block mb-4">
              <span className="bg-green-100 text-green-700 px-3 py-1.5 sm:px-4 sm:py-2 rounded-full text-xs sm:text-sm font-semibold">
                💚 We Hate to Admit It...
              </span>
            </div>
            <h3 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
              It's Free to Use
            </h3>
            <p className="text-2xl text-gray-600 px-4 max-w-3xl mx-auto">
              No monthly fees. No setup costs. Just a tiny 3% fee when customers book—and they pay it, not you.
            </p>
          </div>

          <div className="max-w-3xl mx-auto">
            {/* Minimal Pricing Card */}
            <Card className="bg-white border border-gray-200">
              <CardContent className="px-10 py-16">
                {/* Simple Features List */}
                <div className="mb-10">
                  <h4 className="text-center font-semibold text-gray-900 mb-10 text-xl">Everything Included:</h4>
                  
                  <div className="grid md:grid-cols-2 gap-x-16 gap-y-5 max-w-2xl mx-auto">
                    <div className="flex items-center gap-3">
                      <Check className="w-5 h-5 text-blue-600 flex-shrink-0" />
                      <span className="text-gray-700">Unlimited staff & bookings</span>
                    </div>
                    
                    <div className="flex items-center gap-3">
                      <Check className="w-5 h-5 text-blue-600 flex-shrink-0" />
                      <span className="text-gray-700">AI-Assisted Scheduling</span>
                    </div>
                    
                    <div className="flex items-center gap-3">
                      <Check className="w-5 h-5 text-blue-600 flex-shrink-0" />
                      <span className="text-gray-700">Online Booking Widget</span>
                    </div>
                    
                    <div className="flex items-center gap-3">
                      <Check className="w-5 h-5 text-blue-600 flex-shrink-0" />
                      <span className="text-gray-700">Digital Waivers</span>
                    </div>
                    
                    <div className="flex items-center gap-3">
                      <Check className="w-5 h-5 text-blue-600 flex-shrink-0" />
                      <span className="text-gray-700">Customer Tickets</span>
                    </div>
                    
                    <div className="flex items-center gap-3">
                      <Check className="w-5 h-5 text-blue-600 flex-shrink-0" />
                      <span className="text-gray-700">Payroll Calculator</span>
                    </div>
                    
                    <div className="flex items-center gap-3">
                      <Check className="w-5 h-5 text-blue-600 flex-shrink-0" />
                      <span className="text-gray-700">Reports & Analytics</span>
                    </div>
                    
                    <div className="flex items-center gap-3">
                      <Check className="w-5 h-5 text-blue-600 flex-shrink-0" />
                      <span className="text-gray-700">Staff Portal</span>
                    </div>
                    
                    <div className="flex items-center gap-3">
                      <Check className="w-5 h-5 text-blue-600 flex-shrink-0" />
                      <span className="text-gray-700">REST API</span>
                    </div>
                    
                    <div className="flex items-center gap-3">
                      <Check className="w-5 h-5 text-blue-600 flex-shrink-0" />
                      <span className="text-gray-700">Email Support</span>
                    </div>
                  </div>
                </div>

                <Button 
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium text-lg py-5 rounded-lg shadow-md hover:shadow-lg transition-all duration-200 mb-4"
                  onClick={() => navigate('/signup')}
                >
                  Request Demo <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
                
                <p className="text-center text-sm text-gray-500">
                  Free setup • We'll walk you through everything
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Features Section - MOVED AFTER PRICING */}
      <section id="features" className="bg-gray-50 py-24 animate-on-scroll">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <h3 className="landing-section-title mb-6">
              Everything You Need to Run Your Business
            </h3>
            <p className="landing-section-subtitle">Booking, scheduling, staff management—all in one place</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-10">
            <Card className="bg-white border-none shadow-sm hover:shadow-md transition-shadow">
              <CardHeader>
                <div className="bg-blue-100 w-12 h-12 rounded-lg flex items-center justify-center mb-3">
                  <Zap className="w-6 h-6 text-blue-600" />
                </div>
                <CardTitle className="text-xl">AI-Assisted Scheduling</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">
                  AI suggests optimal staff and resource assignments in seconds. Review, approve, or manually adjust—you're always in control. Saves hours of scheduling work.
                </p>
              </CardContent>
            </Card>

            <Card className="bg-white border-none shadow-sm hover:shadow-md transition-shadow">
              <CardHeader>
                <div className="bg-purple-100 w-12 h-12 rounded-lg flex items-center justify-center mb-3">
                  <Users className="w-6 h-6 text-purple-600" />
                </div>
                <CardTitle className="text-xl">Staff Scheduling</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">
                  Team members manage their own availability and view schedules. Auto-assign staff to bookings based on skills and availability.
                </p>
              </CardContent>
            </Card>

            <Card className="bg-white border-none shadow-sm hover:shadow-md transition-shadow">
              <CardHeader>
                <div className="bg-green-100 w-12 h-12 rounded-lg flex items-center justify-center mb-3">
                  <Calendar className="w-6 h-6 text-green-600" />
                </div>
                <CardTitle className="text-xl">Smart Calendars</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">
                  Calendar, list, and card views with flexible date ranges. 
                  Schedule by day, week, or month with one click.
                </p>
              </CardContent>
            </Card>

            <Card className="bg-white border-none shadow-sm hover:shadow-md transition-shadow">
              <CardHeader>
                <div className="bg-orange-100 w-12 h-12 rounded-lg flex items-center justify-center mb-3">
                  <DollarSign className="w-6 h-6 text-orange-600" />
                </div>
                <CardTitle className="text-xl">Payroll & Tip Pool</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">
                  Automatic payroll calculations with tip pool distribution—calculate each teamMember's daily earnings based on hours worked. Fair, transparent, automatic.
                </p>
              </CardContent>
            </Card>

            <Card className="bg-white border-none shadow-sm hover:shadow-md transition-shadow">
              <CardHeader>
                <div className="bg-cyan-100 w-12 h-12 rounded-lg flex items-center justify-center mb-3">
                  <Package className="w-6 h-6 text-cyan-600" />
                </div>
                <CardTitle className="text-xl">Resources Management</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">
                  Track all your assets, assign team, manage capacity and maintenance schedules.
                </p>
              </CardContent>
            </Card>

            <Card className="bg-white border-none shadow-sm hover:shadow-md transition-shadow">
              <CardHeader>
                <div className="bg-red-100 w-12 h-12 rounded-lg flex items-center justify-center mb-3">
                  <Clock className="w-6 h-6 text-red-600" />
                </div>
                <CardTitle className="text-xl">Real-Time Updates</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">
                  Changes sync instantly. Team see updates immediately. 
                  No more phone calls or text chains.
                </p>
              </CardContent>
            </Card>

            <Card className="bg-white border-none shadow-sm hover:shadow-md transition-shadow">
              <CardHeader>
                <div className="bg-pink-100 w-12 h-12 rounded-lg flex items-center justify-center mb-3">
                  <Sparkles className="w-6 h-6 text-pink-600" />
                </div>
                <CardTitle className="text-xl">Customer Bookings</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">
                  Embeddable booking widget for your website. Customers book online, you confirm with one click. 
                  Team can share personal booking links.
                </p>
              </CardContent>
            </Card>

            <Card className="bg-white border-none shadow-sm hover:shadow-md transition-shadow">
              <CardHeader>
                <div className="bg-emerald-100 w-12 h-12 rounded-lg flex items-center justify-center mb-3">
                  <DollarSign className="w-6 h-6 text-emerald-600" />
                </div>
                <CardTitle className="text-xl">Payment Processing</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">
                  Build sellable services and accept payments instantly. Stripe integration handles checkout, refunds, and payouts. 
                  Customers pay online—funds go straight to your account.
                </p>
              </CardContent>
            </Card>

            <Card className="bg-white border-none shadow-sm hover:shadow-md transition-shadow">
              <CardHeader>
                <div className="bg-sky-100 w-12 h-12 rounded-lg flex items-center justify-center mb-3">
                  <Bell className="w-6 h-6 text-sky-600" />
                </div>
                <CardTitle className="text-xl">Smart Notifications</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">
                  Automated 24-hour reminder emails before trips and thank you messages after completion. 
                  Build customer loyalty with zero effort—professional templates included.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative bg-gradient-to-br from-gray-900 via-slate-900 to-gray-900 py-32 overflow-hidden animate-on-scroll">
        {/* Subtle Background Pattern - NOT blue to avoid clash */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-10 left-10 w-96 h-96 bg-purple-500 rounded-full blur-3xl"></div>
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-indigo-500 rounded-full blur-3xl"></div>
        </div>

        <div className="max-w-4xl mx-auto text-center px-4 relative z-10">
          <h3 className="text-5xl md:text-6xl font-bold text-white mb-6 leading-tight">
            Ready to Grow Your Business?
          </h3>
          <p className="text-2xl md:text-2xl text-gray-300 mb-12 max-w-2xl mx-auto leading-relaxed">
            Join businesses saving 10+ hours every week with smart booking and scheduling.
          </p>
          <Button 
            onClick={() => navigate('/signup')}
            className="bg-gradient-to-r from-blue-600 to-cyan-500 hover:from-blue-700 hover:to-cyan-600 text-white text-xl px-12 py-6 rounded-lg font-medium shadow-2xl shadow-blue-500/30 transform hover:scale-105 transition-all duration-300"
          >
            Request Demo <ArrowRight className="ml-3 w-6 h-6" />
          </Button>
          <p className="text-gray-400 mt-8 text-lg">
            Free setup • Personalized onboarding • No commitment
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-400 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <img 
                  src="/logo-icon.png" 
                  alt="BookingFlows" 
                  className="w-9 h-9 rounded-lg object-cover"
                />
                <h4 className="text-white font-bold text-lg">BookingFlows</h4>
              </div>
              <p className="text-sm">
                Online booking software for tours, venues, experiences, and any business that takes reservations.
              </p>
            </div>
            <div>
              <h5 className="text-white font-semibold mb-4">Product</h5>
              <ul className="space-y-2 text-sm">
                <li><a href="#features" className="hover:text-white transition-colors">Features</a></li>
                <li><a href="#pricing" className="hover:text-white transition-colors">Pricing</a></li>
                <li><button onClick={() => navigate('/login')} className="hover:text-white transition-colors">Login</button></li>
              </ul>
            </div>
            <div>
              <h5 className="text-white font-semibold mb-4">Support</h5>
              <ul className="space-y-2 text-sm">
                <li><a href="mailto:support@bookingflows.zendesk.com" className="hover:text-white transition-colors">Contact Us</a></li>
              </ul>
            </div>
            <div>
              <h5 className="text-white font-semibold mb-4">Connect</h5>
              <SocialLinksDark />
              <p className="text-xs text-gray-500 mt-3">
                <a href="https://solo.to/bookingflows" target="_blank" rel="noopener noreferrer" className="hover:text-gray-300 transition-colors">
                  solo.to/bookingflows
                </a>
              </p>
            </div>
          </div>
          <div className="border-t border-gray-800 mt-8 pt-8 flex flex-col sm:flex-row items-center justify-between gap-3 text-sm">
            <p>&copy; {new Date().getFullYear()} BookingFlows. All rights reserved.</p>
            <div className="flex gap-4">
              <button onClick={() => navigate('/privacy')} className="hover:text-white transition-colors">Privacy</button>
              <button onClick={() => navigate('/terms')} className="hover:text-white transition-colors">Terms</button>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
